import React, { useState, useRef, useEffect } from 'react';
import { Minus, Plus, AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { motion, AnimatePresence } from 'framer-motion';

export const CustomizationModal = ({ item, open, onClose, onAdd }) => {
  const [quantity, setQuantity] = useState(1);
  const [selections, setSelections] = useState({});
  const [groupErrors, setGroupErrors] = useState({});
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const optionRefs = useRef({});

  // Reset state when modal opens with new item
  useEffect(() => {
    if (open) {
      setQuantity(1);
      setSelections({});
      setGroupErrors({});
      setHasAttemptedSubmit(false);
    }
  }, [open, item?.id]);

  const handleSelectionChange = (optionId, choice) => {
    setSelections(prev => ({
      ...prev,
      [optionId]: choice
    }));
    // Clear error for this group immediately when user selects
    setGroupErrors(prev => ({
      ...prev,
      [optionId]: false
    }));
  };

  const handleCheckboxChange = (optionId, choice, checked) => {
    setSelections(prev => {
      const current = prev[optionId] || [];
      if (checked) {
        return {
          ...prev,
          [optionId]: [...current, choice]
        };
      } else {
        return {
          ...prev,
          [optionId]: current.filter(c => c.name !== choice.name)
        };
      }
    });
    // Clear error when user makes any selection
    setGroupErrors(prev => ({
      ...prev,
      [optionId]: false
    }));
  };

  const calculateTotalPrice = () => {
    if (!item) return 0;
    let total = item.price;
    
    Object.values(selections).forEach(selection => {
      if (Array.isArray(selection)) {
        selection.forEach(choice => {
          total += choice.price || 0;
        });
      } else if (selection && selection.price) {
        total += selection.price;
      }
    });
    
    return total * quantity;
  };

  // Get list of missing required groups
  const getMissingRequiredGroups = () => {
    if (!item?.customization_options || item.customization_options.length === 0) {
      return [];
    }

    return item.customization_options
      .filter(opt => opt.required)
      .filter(opt => {
        const selection = selections[opt.id];
        if (Array.isArray(selection)) {
          return selection.length === 0;
        }
        return !selection;
      });
  };

  const missingGroups = getMissingRequiredGroups();
  const canAdd = missingGroups.length === 0;

  const handleButtonClick = () => {
    if (canAdd) {
      // All required selections made - add to cart
      onAdd({
        ...item,
        quantity,
        customizations: selections,
        finalPrice: calculateTotalPrice() / quantity
      });
      setQuantity(1);
      setSelections({});
      setGroupErrors({});
      setHasAttemptedSubmit(false);
      onClose();
    } else {
      // Missing required selections - highlight them
      setHasAttemptedSubmit(true);
      
      // Set error state for all missing groups
      const newErrors = {};
      missingGroups.forEach(group => {
        newErrors[group.id] = true;
      });
      setGroupErrors(newErrors);

      // Scroll to first missing group
      const firstMissingId = missingGroups[0]?.id;
      if (firstMissingId && optionRefs.current[firstMissingId]) {
        optionRefs.current[firstMissingId].scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }
    }
  };

  if (!item) return null;

  const sortedOptions = [...(item.customization_options || [])].sort((a, b) => a.sequence - b.sequence);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="customization-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">{item.name}</DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          <div>
            <p className="text-muted-foreground">{item.description}</p>
            <p className="text-2xl font-bold text-primary mt-2">£{item.price.toFixed(2)}</p>
          </div>

          {/* Missing selections banner */}
          <AnimatePresence>
            {hasAttemptedSubmit && missingGroups.length > 0 && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2"
              >
                <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-red-800">
                    Please complete your selection
                  </p>
                  <p className="text-sm text-red-600">
                    Missing: {missingGroups.map(g => g.title || 'Selection').join(', ')}
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {sortedOptions.length > 0 && (
            <div className="space-y-6">
              {sortedOptions.map((option) => {
                const hasError = groupErrors[option.id];
                const isSelected = option.type === 'radio' 
                  ? !!selections[option.id]
                  : (selections[option.id]?.length || 0) > 0;

                return (
                  <motion.div
                    key={option.id}
                    ref={el => optionRefs.current[option.id] = el}
                    animate={hasError ? { x: [0, -10, 10, -10, 10, 0] } : {}}
                    transition={{ duration: 0.4 }}
                    className={`border-t pt-4 rounded-lg transition-all duration-300 ${
                      hasError 
                        ? 'border border-red-300 bg-red-50/50 p-4 -mx-2' 
                        : ''
                    }`}
                  >
                    <div className="flex justify-between items-center mb-3">
                      <h3 className="font-bold text-lg">
                        {option.title || 'Please select'}
                      </h3>
                      <span className={`text-sm px-2 py-0.5 rounded-full ${
                        option.required 
                          ? hasError
                            ? 'bg-red-100 text-red-700 font-medium'
                            : isSelected
                              ? 'bg-green-100 text-green-700'
                              : 'bg-orange-100 text-orange-700'
                          : 'text-muted-foreground'
                      }`}>
                        {option.required ? (
                          <>
                            {hasError && <AlertCircle className="h-3 w-3 inline mr-1" />}
                            Required
                          </>
                        ) : 'Optional'}
                      </span>
                    </div>
                    
                    <p className="text-sm text-muted-foreground mb-3">
                      Choose {option.type === 'radio' ? '1' : 'any'}
                    </p>

                    {/* Error helper text */}
                    <AnimatePresence>
                      {hasError && (
                        <motion.p
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className="text-sm text-red-600 mb-3 flex items-center gap-1"
                        >
                          <AlertCircle className="h-4 w-4" />
                          Please select {option.type === 'radio' ? '1 option' : 'at least 1 option'}
                        </motion.p>
                      )}
                    </AnimatePresence>

                    {option.type === 'radio' ? (
                      <RadioGroup
                        value={selections[option.id]?.name}
                        onValueChange={(value) => {
                          const choice = option.choices.find(c => c.name === value);
                          handleSelectionChange(option.id, choice);
                        }}
                      >
                        <div className="space-y-2">
                          {option.choices.map((choice) => (
                            <div 
                              key={choice.name} 
                              role="button"
                              tabIndex={0}
                              onClick={() => handleSelectionChange(option.id, choice)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  handleSelectionChange(option.id, choice);
                                }
                              }}
                              className={`flex items-center justify-between p-3 rounded-lg transition-colors cursor-pointer ${
                                selections[option.id]?.name === choice.name
                                  ? 'bg-primary/10 border border-primary/30'
                                  : 'hover:bg-secondary/50'
                              }`}
                            >
                              <div className="flex items-center space-x-3 pointer-events-none">
                                <RadioGroupItem value={choice.name} id={`${option.id}-${choice.name}`} />
                                <Label htmlFor={`${option.id}-${choice.name}`} className="cursor-pointer font-medium">
                                  {choice.name}
                                </Label>
                              </div>
                              {choice.price > 0 && (
                                <span className="text-sm font-medium pointer-events-none">+£{choice.price.toFixed(2)}</span>
                              )}
                            </div>
                          ))}
                        </div>
                      </RadioGroup>
                    ) : (
                      <div className="space-y-2">
                        {option.choices.map((choice) => {
                          const isChecked = (selections[option.id] || []).some(c => c.name === choice.name);
                          return (
                            <div 
                              key={choice.name} 
                              role="button"
                              tabIndex={0}
                              onClick={() => handleCheckboxChange(option.id, choice, !isChecked)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  handleCheckboxChange(option.id, choice, !isChecked);
                                }
                              }}
                              className={`flex items-center justify-between p-3 rounded-lg transition-colors cursor-pointer ${
                                isChecked
                                  ? 'bg-primary/10 border border-primary/30'
                                  : 'hover:bg-secondary/50'
                              }`}
                            >
                              <div className="flex items-center space-x-3 pointer-events-none">
                                <input
                                  type="checkbox"
                                  id={`${option.id}-${choice.name}`}
                                  checked={isChecked}
                                  readOnly
                                  className="w-4 h-4 rounded accent-primary"
                                />
                                <Label htmlFor={`${option.id}-${choice.name}`} className="cursor-pointer font-medium">
                                  {choice.name}
                                </Label>
                              </div>
                              {choice.price > 0 && (
                                <span className="text-sm font-medium pointer-events-none">+£{choice.price.toFixed(2)}</span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </motion.div>
                );
              })}
            </div>
          )}

          <div className="flex items-center justify-between border-t pt-4">
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                size="icon"
                className="h-10 w-10 rounded-full"
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                data-testid="decrease-quantity"
              >
                <Minus className="h-4 w-4" />
              </Button>
              <span className="text-xl font-bold w-8 text-center" data-testid="quantity-display">{quantity}</span>
              <Button
                variant="outline"
                size="icon"
                className="h-10 w-10 rounded-full"
                onClick={() => setQuantity(quantity + 1)}
                data-testid="increase-quantity"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            data-testid="add-to-cart-final"
            className={`w-full rounded-full h-12 text-base transition-all ${
              !canAdd && hasAttemptedSubmit
                ? 'bg-orange-500 hover:bg-orange-600'
                : ''
            }`}
            onClick={handleButtonClick}
          >
            {canAdd ? (
              <>ADD - £{calculateTotalPrice().toFixed(2)}</>
            ) : (
              <>
                <AlertCircle className="h-4 w-4 mr-2" />
                Select required options ({missingGroups.length} remaining)
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
